import React from 'react';
import ColorEditor from '../ColorEditor';

const CTAEditor = ({ content, onContentChange, onMultipleContentChanges, site, onCreateCustomTheme }) => {
  const handleCtaChange = (ctaType, field, value) => {
    const currentCta = content[ctaType] || {};
    onContentChange(ctaType, { ...currentCta, [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Headline</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Ready to Get Started?"
          maxLength={80}
        />
        <div className="text-xs text-gray-500 mt-1">
          {(content.title || '').length} / 80 characters
        </div>
      </div>

      {/* Section Subtitle */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Supporting text</label>
        <input
          type="text"
          value={content.subtitle || ''}
          onChange={(e) => onContentChange('subtitle', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Join thousands of satisfied customers today"
          maxLength={120}
        />
        <div className="text-xs text-gray-500 mt-1">
          {(content.subtitle || '').length} / 120 characters
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Value proposition</label>
        <textarea
          rows={3}
          value={content.description || ''}
          onChange={(e) => onContentChange('description', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Describe the key value and benefits customers will receive"
          maxLength={200}
        />
        <div className="text-xs text-gray-500 mt-1">
          {(content.description || '').length} / 200 characters
        </div>
      </div>

      {/* Primary CTA */}
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <h4 className="text-xs font-medium text-gray-900">Primary Action</h4>
        
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Button text</label>
          <input
            type="text"
            value={content.primaryCta?.text || ''}
            onChange={(e) => handleCtaChange('primaryCta', 'text', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Get Started Now"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
          <select
            value={content.primaryCta?.action || 'contact'}
            onChange={(e) => handleCtaChange('primaryCta', 'action', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="contact">Go to contact section</option>
            <option value="phone">Call phone number</option>
            <option value="email">Send email</option>
            <option value="booking">Open booking form</option>
            <option value="purchase">Start purchase process</option>
            <option value="signup">Sign up for service</option>
            <option value="demo">Request demo</option>
            <option value="consultation">Book consultation</option>
          </select>
        </div>
      </div>

      {/* Secondary CTA */}
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <h4 className="text-xs font-medium text-gray-900">Secondary Action (Optional)</h4>
        
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Button text</label>
          <input
            type="text"
            value={content.secondaryCta?.text || ''}
            onChange={(e) => handleCtaChange('secondaryCta', 'text', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="Learn More"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
          <select
            value={content.secondaryCta?.action || 'faq'}
            onChange={(e) => handleCtaChange('secondaryCta', 'action', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="faq">Go to FAQ section</option>
            <option value="services">View services</option>
            <option value="portfolio">View portfolio</option>
            <option value="pricing">See pricing</option>
            <option value="testimonials">Read testimonials</option>
            <option value="blog">Visit blog</option>
            <option value="faq">View FAQ</option>
            <option value="contact">Contact us</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!(content.secondaryCta?.text)}
              onChange={(e) => {
                if (e.target.checked) {
                  handleCtaChange('secondaryCta', 'text', '');
                } else {
                  handleCtaChange('secondaryCta', 'text', 'Learn More');
                }
              }}
              className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
            />
            Hide secondary button
          </label>
        </div>
      </div>

      {/* Design Options */}
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <h4 className="text-xs font-medium text-gray-900">Design Style</h4>
        
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Layout style</label>
          <select
            value={content.layout || 'centered'}
            onChange={(e) => onContentChange('layout', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="centered">Centered layout</option>
            <option value="split">Split layout</option>
            <option value="banner">Banner style</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Urgency level</label>
          <select
            value={content.urgency || 'medium'}
            onChange={(e) => onContentChange('urgency', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="low">Low urgency (casual)</option>
            <option value="medium">Medium urgency (encouraging)</option>
            <option value="high">High urgency (compelling)</option>
          </select>
        </div>
      </div>

      {/* Preview Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3">
        <h4 className="text-xs font-medium text-blue-900 mb-1">💡 CTA Best Practices</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Use action-oriented language in buttons</li>
          <li>• Focus on the value customers receive</li>
          <li>• Keep headlines short and compelling</li>
          <li>• Test different urgency levels for your audience</li>
        </ul>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default CTAEditor;